/**
 * The itemised breakdown a professional attaches to a quote (spec
 * rfq-structured-quotes §3a).
 *
 * Lives in the service layer rather than beside either card because BOTH
 * sides read it: the consumer comparing bids and the professional reviewing
 * their own. One shape, one set of labels, one renderer.
 */

/**
 * MIRRORS the backend's QUOTE_LINE_ITEM_KINDS. The two are hand-kept, which is
 * exactly how `envio` was nearly stranded: the backend added it and this list
 * did not, so no professional could have produced a shipping line and a
 * physical job would have shipped with nobody charged and nobody reimbursed.
 */
export const QUOTE_LINE_ITEM_KINDS = [
  "honorarios",
  "timbres_aranceles",
  "gastos",
  "envio",
] as const;
export type QuoteLineItemKind = (typeof QUOTE_LINE_ITEM_KINDS)[number];

export type QuoteUrType = "dgi" | "caja_notarial";

export interface QuoteLineItemView {
  description: string;
  kind: QuoteLineItemKind;
  /** Always present and always authoritative — a UR line was converted at submission. */
  amountMinor: number;
  urUnits?: number;
  urType?: QuoteUrType;
  urValueMinorAtQuote?: number;
}

/** i18n key for a kind, so every surface names it the same way. */
export const QUOTE_LINE_ITEM_KIND_LABEL_KEYS: Record<QuoteLineItemKind, string> = {
  honorarios: "quoteBreakdown.kindHonorarios",
  timbres_aranceles: "quoteBreakdown.kindTimbres",
  gastos: "quoteBreakdown.kindGastos",
  envio: "quoteBreakdown.kindEnvio",
};

export const QUOTE_UR_TYPE_LABEL_KEYS: Record<QuoteUrType, string> = {
  dgi: "quoteBreakdown.indexDgi",
  caja_notarial: "quoteBreakdown.indexCaja",
};

/**
 * Does the per-line detail reveal anything the subtotals do not?
 *
 * The breakdown shows one subtotal per KIND and, behind a toggle, one row per
 * LINE. With a single line in each kind those are the same rows — same label,
 * same amount — so the toggle promised "ver detalle" and delivered the figures
 * the reader was already looking at, on both the consumer's bid list and the
 * professional's own card.
 *
 * It earns its place in exactly two cases:
 *  - a kind holds SEVERAL lines, so the split between them is genuinely hidden;
 *  - a line was priced in UR, so the conversion ("12 UR · $ 1.440") is.
 *
 * Otherwise the only thing the detail added was the professional's own
 * description, which the subtotal row carries inline instead.
 *
 * Lives here rather than in the component so the rule can be tested without a
 * renderer — it decides what a reader sees, which makes it worth pinning.
 */
export function quoteDetailRevealsMore(lineItems: QuoteLineItemView[] | null): boolean {
  if (lineItems == null || lineItems.length === 0) return false;
  const kinds = new Set(lineItems.map((item) => item.kind));
  if (lineItems.length > kinds.size) return true;
  return lineItems.some((item) => item.urUnits != null && item.urType != null);
}
